/** PRD §10.3 — max assets (uploads + URLs) per report. Mirrors API cap. */
export const MAX_ASSETS_PER_REPORT = 25;

const URL_START = /^(https?:\/\/|www\.)/i;
/** Bare domain: token.tld[/...] e.g. cdn.example.com/x.png */
const BARE_DOMAIN = /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/|$|\?)/i;
const MARKDOWN_LINK = /^\[[^\]]*\]\((.+)\)$/;
const ANGLE_BRACKETS = /^<([^>]+)>$/;
const SURROUNDING_QUOTES = /^["'](.+)["']$/;
const LEADING_WRAPPER = /^[\[(<"'`]+/;
const TRAILING_JUNK = /[)\]>.,;:;"'`]+$/;

function looksLikeUrlStart(value: string): boolean {
  return URL_START.test(value) || BARE_DOMAIN.test(value);
}

/** Strip markdown wrappers, quotes, brackets, and sentence punctuation. */
export function sanitizeToken(raw: string): string {
  let token = raw.trim();
  if (!token) return "";

  const markdown = token.match(MARKDOWN_LINK);
  if (markdown) token = markdown[1].trim();

  const angle = token.match(ANGLE_BRACKETS);
  if (angle) token = angle[1].trim();

  const quoted = token.match(SURROUNDING_QUOTES);
  if (quoted) token = quoted[1].trim();

  token = token.replace(LEADING_WRAPPER, "").replace(TRAILING_JUNK, "");
  return token.trim();
}

/**
 * Split raw pasted text into candidate URL tokens.
 *
 * Newlines and whitespace are always delimiters. Commas are only treated as
 * delimiters when the text after the comma looks like the start of a new URL —
 * so `a.com,b.com` splits, but `x.com/?ids=1,2,3` stays intact.
 */
export function splitTokens(raw: string): string[] {
  if (!raw) return [];
  const chunks = raw.split(/[\s\n\r\t]+/).filter(Boolean);
  const out: string[] = [];

  for (const chunk of chunks) {
    if (!chunk.includes(",")) {
      out.push(chunk);
      continue;
    }

    const parts = chunk.split(",");
    let buf = parts[0] ?? "";
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i] ?? "";
      if (looksLikeUrlStart(sanitizeToken(part))) {
        out.push(buf);
        buf = part;
      } else {
        buf += "," + part;
      }
    }
    out.push(buf);
  }

  return out
    .map((token) => token.trim().replace(/^,+|,+$/g, ""))
    .filter(Boolean);
}

/**
 * Canonical form used for de-duplication: lowercased scheme + host, trailing
 * slash on an empty path dropped. Query/fragment preserved.
 */
export function normalizeUrl(token: string): string | null {
  const sanitized = sanitizeToken(token);
  if (!sanitized || !looksLikeUrlStart(sanitized)) return null;

  const hadScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(sanitized);
  let candidate = sanitized;
  if (!hadScheme) {
    candidate = "https://" + candidate;
  }

  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    url.protocol = url.protocol.toLowerCase();
    url.hostname = url.hostname.toLowerCase();
    if (url.pathname === "/") url.pathname = "";
    return url.toString();
  } catch {
    return null;
  }
}

export type UrlInvalidReason =
  | "incomplete"
  | "unsupported_scheme"
  | "missing_asset_path";

/** A homepage/domain-only URL is not a fetchable creative asset. */
function hasAssetPath(parsed: URL): boolean {
  if (parsed.pathname.length > 1) return true;
  return parsed.search.length > 0;
}

export interface ClassifiedUrl {
  raw: string;
  url?: string;
  normalized?: string;
  valid: boolean;
  reason?: UrlInvalidReason;
  assumedScheme?: boolean;
}

export function classifyUrl(raw: string): ClassifiedUrl | null {
  const cleaned = sanitizeToken(raw);
  if (!cleaned) return null;

  // Detect explicit non-http(s) scheme (e.g. ftp://, rtsp://) before the
  // looksLikeUrlStart guard — these would silently fall through to unrecognized otherwise.
  const schemeMatch = cleaned.match(/^([a-z][a-z0-9+.-]*):\/\//i);
  if (schemeMatch) {
    const scheme = schemeMatch[1]!.toLowerCase();
    if (scheme !== "http" && scheme !== "https") {
      return { raw, valid: false, reason: "unsupported_scheme" };
    }
  }

  if (!looksLikeUrlStart(cleaned)) return null;

  const hadScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(cleaned);
  const normalized = normalizeUrl(cleaned);
  if (!normalized) {
    return {
      raw,
      valid: false,
      reason: "incomplete",
    };
  }

  const parsed = new URL(normalized);
  if (!hasAssetPath(parsed)) {
    return {
      raw,
      valid: false,
      reason: "missing_asset_path",
    };
  }

  return {
    raw,
    url: normalized,
    normalized,
    valid: true,
    assumedScheme: !hadScheme,
  };
}

export interface ParsedUrlEntry {
  url: string;
  assumedScheme: boolean;
}

export interface ParsedInvalidEntry {
  raw: string;
  reason: UrlInvalidReason;
}

export interface ExtractUrlsOptions {
  /** Total asset slots already consumed (uploads + confirmed URLs). */
  existingAssetCount?: number;
  /** Confirmed URL strings already in the draft (for de-duplication). */
  existingUrls?: string[];
  maxAssets?: number;
}

export interface ExtractUrlsResult {
  urls: ParsedUrlEntry[];
  invalid: ParsedInvalidEntry[];
  /** Valid URLs dropped because the asset cap was reached. */
  skipped: string[];
  /** Valid URLs that duplicate an existing or earlier-in-paste entry. */
  duplicates: string[];
  /** Tokens with no URL detected when nothing else was extracted from the paste. */
  unrecognized: string[];
}

/**
 * Extract a clean, de-duplicated list of asset URLs from arbitrary pasted text.
 * Non-URL prose is ignored. URL-like tokens that fail validation go to `invalid`.
 */
export function extractUrls(
  raw: string,
  {
    existingAssetCount = 0,
    existingUrls = [],
    maxAssets = MAX_ASSETS_PER_REPORT,
  }: ExtractUrlsOptions = {},
): ExtractUrlsResult {
  const tokens = splitTokens(raw);
  const urls: ParsedUrlEntry[] = [];
  const invalid: ParsedInvalidEntry[] = [];
  const skipped: string[] = [];
  const duplicates: string[] = [];
  const ignoredTokens: string[] = [];

  const seen = new Set(
    existingUrls
      .map((url) => normalizeUrl(url))
      .filter((url): url is string => url !== null),
  );

  const remainingSlots = Math.max(0, maxAssets - existingAssetCount);

  for (const token of tokens) {
    const classified = classifyUrl(token);
    if (!classified) {
      if (sanitizeToken(token)) ignoredTokens.push(token);
      continue;
    }

    if (!classified.valid || !classified.url || !classified.normalized) {
      invalid.push({
        raw: classified.raw,
        reason: classified.reason ?? "incomplete",
      });
      continue;
    }

    if (seen.has(classified.normalized)) {
      duplicates.push(classified.raw);
      continue;
    }
    seen.add(classified.normalized);

    if (urls.length >= remainingSlots) {
      skipped.push(classified.url);
      continue;
    }

    urls.push({
      url: classified.url,
      assumedScheme: classified.assumedScheme ?? false,
    });
  }

  const unrecognized =
    urls.length === 0 && invalid.length === 0 ? ignoredTokens : [];

  return { urls, invalid, skipped, duplicates, unrecognized };
}

export interface ParsedUrlInput {
  /** Valid, de-duplicated URLs not already present — safe to add. */
  valid: string[];
  /** URL-like tokens that could not be resolved to a valid http(s) URL. */
  invalid: ParsedInvalidEntry[];
  /** Valid URLs that already exist (in the draft or earlier in the same paste). */
  duplicates: string[];
  /** Valid URLs dropped because the asset cap was reached. */
  skipped: string[];
  /** Tokens with no URL detected when nothing else was extracted from the paste. */
  unrecognized: string[];
}

export interface ParseUrlInputOptions extends ExtractUrlsOptions {}

/**
 * Adapter for the draft store and input card. Maps {@link extractUrls} into the
 * shape consumed by preflight draft/UI code.
 */
export function parseUrlInput(
  raw: string,
  existing: string[],
  options: ParseUrlInputOptions = {},
): ParsedUrlInput {
  const result = extractUrls(raw, {
    ...options,
    existingUrls: existing,
  });

  return {
    valid: result.urls.map((entry) => entry.url),
    invalid: result.invalid,
    duplicates: result.duplicates,
    skipped: result.skipped,
    unrecognized: result.unrecognized,
  };
}
